import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { Search } from "lucide-react";
import { fetchRecipesPage } from "../../services/catalog.api";
import LoadMore from "../shared/LoadMore";
import RecipeCard from "../shared/RecipeCard";
import "./index.css";

export default function AllRecipes() {
  const [search, setSearch] = useState("");
  // Searching server-side means results are no longer capped at whatever
  // happened to be on the first page.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["recipes", debouncedSearch],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchRecipesPage({
        search: debouncedSearch || undefined,
        page: pageParam,
        limit: 24,
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
  });

  if (error) toast.error(error?.message);

  const filtered = useMemo(
    () => (data?.pages || []).flatMap((p) => p.items),
    [data],
  );
  const total = data?.pages?.[0]?.pagination?.total ?? filtered.length;

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <h5 style={{ fontWeight: 800, fontSize: 22, margin: 0, fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
          All Recipes
        </h5>
        {!isPending && (
          <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
            {total} recipe{total !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 24 }}>
        <Search
          size={16}
          color="#9ca3af"
          style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
        />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search recipes or chef name…"
          style={{
            width: "100%",
            padding: "11px 14px 11px 38px",
            borderRadius: 10,
            border: "1.5px solid #e5e7eb",
            fontSize: 14,
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            color: "#111827",
            background: "#f9fafb",
            outline: "none",
            boxSizing: "border-box",
            transition: "border-color 0.15s, background 0.15s",
          }}
          onFocus={e => { e.target.style.borderColor = "#1b6dc1"; e.target.style.background = "#fff"; }}
          onBlur={e => { e.target.style.borderColor = "#e5e7eb"; e.target.style.background = "#f9fafb"; }}
        />
      </div>

      {/* Empty state */}
      {!isPending && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", fontFamily: "'Inter', sans-serif" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📖</div>
          <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No recipes found</p>
          <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>
            {search ? "Try a different search term" : "No recipes available yet"}
          </p>
        </div>
      )}

      {/* Grid */}
      <div className="all-recipes-grid">
        {isPending
          ? [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Skeleton key={i} height={260} borderRadius={12} />
            ))
          : filtered.map(recipe => (
              <RecipeCard key={recipe._id} item={recipe} />
            ))
        }
      </div>

      {!isPending && (
        <LoadMore
          hasMore={hasNextPage}
          isLoading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
          label="Load more recipes"
        />
      )}
    </div>
  );
}
